import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '../../../assets/ArrowLeftIcon';
import { Button } from '../../../components/Button';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import type { RosterUploadDetail as Detail } from '../../../types/adminRosterUploads';
import { AdminPage, AdminPageHeader, AdminPanel } from '../AdminPage';
import { RosterUploadFiles } from './RosterUploadFiles';
import { RosterUploadMetrics } from './RosterUploadMetrics';
import { RosterUploadPlayersTable } from './RosterUploadPlayersTable';
import { RosterUploadProspects } from './RosterUploadProspects';
import { RosterUploadStatusBadge } from './RosterUploadStatusBadge';
import {
  EMPTY_CELL,
  filesLabel,
  formatInstant,
  formatRelative,
  sourceLabel,
  uploadTypeLabel,
} from './rosterUploadFormat';
import { resolveRosterUploadsBackTarget, rosterUploadsHref } from './rosterUploadsNavigation';
import { useRosterUpload } from './useRosterUploads';
import '../../../components/DataTable.css';
import './RosterUploads.css';

/*
  Rendered as the page's first child in every state, not through AdminPageHeader's `back`, so the
  same link element survives the switch from loading to loaded instead of being remounted.
*/
const BackLink = ({ to }: { to: string }) => (
  <Link className="roster-upload__back" to={to}>
    <ArrowLeftIcon className="roster-upload__back-icon" />
    Back to Uploaded Rosters
  </Link>
);

const UploadHeader = ({ upload, now }: { upload: Detail; now: Date }) => (
  <>
    <AdminPageHeader
      title={
        <>
          {upload.organizationName || 'Unnamed organization'}
          <span className="roster-upload__title-suffix"> roster upload</span>
        </>
      }
      meta={<RosterUploadStatusBadge status={upload.status} />}
    />

    <AdminPanel>
      <dl className="roster-upload__facts" data-testid="roster-upload-facts">
        <div className="roster-upload__fact">
          <dt>Organization</dt>
          <dd className="roster-upload__fact-links">
            <Link to={rosterUploadsHref({ organizationId: upload.organizationId })}>
              All uploads from this organization
            </Link>
            <Link to={`/admin/organizations/${upload.organizationId}`}>Organization details</Link>
          </dd>
        </div>
        <div className="roster-upload__fact">
          <dt>Uploaded by</dt>
          <dd>
            {upload.uploadedBy ? (
              <>
                {upload.uploadedBy.name ?? upload.uploadedBy.email}
                {upload.uploadedBy.name && (
                  <span className="roster-uploads__secondary"> {upload.uploadedBy.email}</span>
                )}
              </>
            ) : (
              'Unknown'
            )}
          </dd>
        </div>
        <div className="roster-upload__fact">
          <dt>Uploaded</dt>
          <dd>
            <time dateTime={upload.createdAt}>{formatInstant(upload.createdAt)}</time>
            <span className="roster-uploads__secondary"> ({formatRelative(upload.createdAt, now)})</span>
          </dd>
        </div>
        <div className="roster-upload__fact">
          <dt>Source</dt>
          <dd>
            {sourceLabel(upload.sourceType)} · {filesLabel(upload.fileCount)}
          </dd>
        </div>
        <div className="roster-upload__fact">
          <dt>Upload type</dt>
          <dd>{uploadTypeLabel(upload.uploadType)}</dd>
        </div>
        <div className="roster-upload__fact">
          <dt>Teams</dt>
          <dd>{upload.teamNames.length > 0 ? upload.teamNames.join(', ') : EMPTY_CELL}</dd>
        </div>
      </dl>
    </AdminPanel>
  </>
);

/**
 * One roster upload: who, when, what the extraction found and how it got there.
 *
 * "Back" returns to the list as it was — filters and page included — from route state the list
 * hands over, validated so a hand-edited history entry cannot send the admin anywhere but the list.
 */
export const RosterUploadDetail = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const backTo = resolveRosterUploadsBackTarget(location.state);
  const { state, reload } = useRosterUpload(id);
  // Relative time is measured from when the page opened; it does not need to tick.
  const [now] = useState(() => new Date());

  useDocumentTitle(
    state.kind === 'loaded'
      ? `${state.upload.organizationName || 'Roster'} upload`
      : state.kind === 'not-found'
        ? 'Roster upload not found'
        : null
  );

  if (state.kind === 'loading') {
    return (
      <AdminPage className="roster-upload">
        <BackLink to={backTo} />
        <p className="admin-state" role="status">
          Loading roster upload...
        </p>
      </AdminPage>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <AdminPage className="roster-upload">
        <BackLink to={backTo} />
        <div data-testid="roster-upload-not-found">
          <AdminPageHeader
            title="Roster upload not found"
            subtitle="It may have been deleted, or the link is wrong. The list shows every upload that still exists."
          />
        </div>
      </AdminPage>
    );
  }

  if (state.kind === 'error') {
    return (
      <AdminPage className="roster-upload">
        <BackLink to={backTo} />
        <div className="admin-error" role="alert">
          <span>{state.message}</span>
          <Button type="button" variant="secondary" onClick={reload}>
            Try again
          </Button>
        </div>
      </AdminPage>
    );
  }

  const { upload } = state;

  return (
    <AdminPage className="roster-upload">
      <BackLink to={backTo} />
      <UploadHeader upload={upload} now={now} />

      {(upload.hasError || upload.errorMessage) && (
        <div
          className="admin-error"
          data-testid="roster-upload-error"
        >
          <span>
            <strong>Extraction error: </strong>
            {upload.errorMessage ?? 'The extraction failed without recording a reason.'}
          </span>
        </div>
      )}

      <div className="roster-upload__grid">
        <RosterUploadMetrics upload={upload} />
        <RosterUploadFiles files={upload.files} signedUrlExpiresAt={upload.signedUrlExpiresAt} />
      </div>

      <RosterUploadPlayersTable
        columns={upload.columns}
        players={upload.players}
        truncated={upload.playersTruncated}
        playerCount={upload.playerCount}
      />

      <RosterUploadProspects
        key={upload.id}
        uploadId={upload.id}
        organizationId={upload.organizationId}
        organizationName={upload.organizationName}
      />
    </AdminPage>
  );
};
